import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../../common/SafeIcon';
import EventSubmissionModal from '../EventSubmissionModal';

const { FiShoppingBag, FiCalendar, FiSpeaker, FiUsers, FiCheck, FiStar, FiZap } = FiIcons;

const PartnerWithUs = () => {
  const [ref, inView] = useInView({ threshold: 0.1, triggerOnce: true });
  const [isProYearly, setIsProYearly] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);

  const partners = [
    {
      icon: FiShoppingBag,
      title: 'Queer-Owned Businesses',
      description: 'Partner with us to reach your community and grow your business.',
    },
    {
      icon: FiCalendar,
      title: 'Event Organisers',
      description: 'Promote your LGBTQIA+ events and connect with eager attendees.',
    },
    {
      icon: FiSpeaker,
      title: 'Content Creators',
      description: 'Amplify your voice and reach a supportive, engaged audience.',
    },
    {
      icon: FiUsers,
      title: 'Community Partners',
      description: 'Collaborate with us to strengthen the queer ecosystem.',
    },
  ];

  const pricingPlans = [
    {
      name: 'Lite',
      price: '£0',
      frequency: 'per month',
      description: 'For community, solo queer-owned, micro & established small businesses.',
      features: [
        'One business listing (Togethr Network).',
        '2 images (logo or storefront).',
        'Display in local search results (below paid tiers).',
        'Multi event listings (Togethr Network).',
      ],
      buttonText: 'Get Started',
      buttonStyle: 'bg-white border border-gray-300 text-gray-900 hover:bg-gray-50 focus-ring',
      isModal: true,
      isPopular: false,
    },
    {
      name: 'Pro',
      price: { 
        monthly: { original: '£39', discounted: '£25' }, 
        yearly: { original: '£390', discounted: '£250' }
      },
      frequency: { monthly: 'month', yearly: 'year' },
      description: 'For growing brands, SMEs and businesses that want messaging directory visibility.',
      features: [
        'Everything in Lite plus:',
        'Verified LGBTQIA+ or Ally Badge (with vetting).',
        'Logo, gallery and bio (up to 5 images, 250 words).',
        'Up to 3 categories (e.g. Retail, Wellness, Food).',
        'Included in messaging directory.',
        'Multi event listings (Messaging Directory/Web/Together Network).',
        'Priority placement above Lite listings.',
        'Feature/spotlight rotation in market and events space (desktop and app).',
      ],
      buttonText: 'Choose Pro',
      buttonStyle: 'bg-primary text-white hover:bg-primary/90 focus-ring',
      buttonLink: 'https://buy.stripe.com/8x2dR9dfIc8f7mdePKfIs00?prefilled_promo_code=PRO14OFF',
      isPopular: true,
      hasDiscount: true,
    },
    {
      name: 'Strategic',
      price: "Contact Us",
      frequency: '',
      description: 'For corporates & sponsors - LGBTQIA+/Ally.',
      features: [
        'All Pro benefits plus:',
        'Top-of-category placement (first spots in search results).',
        'Homepage rotation feature ("Business of the Week").',
        'Inclusion in WhatsApp broadcast/month (to subscribers).',
        'Enhanced analytics report (clicks + message rates).',
        'Bespoke campaigns.',
        'Priority support and account manager.',
      ],
      buttonText: 'Contact Us',
      buttonStyle: 'bg-white border border-gray-300 text-gray-900 hover:bg-gray-50 focus-ring',
      buttonLink: 'https://togethr.formaloo.me/queerbizdirectory',
      isPopular: false,
    },
  ];

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1,
        delayChildren: 0.2,
      },
    },
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 30 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.6, ease: 'easeOut' },
    },
  };

  const handleButtonClick = (plan) => {
    if (plan.isModal) {
      setIsModalOpen(true);
    }
  };

  return (
    <section ref={ref} className="py-24 bg-white" aria-labelledby="partner-heading">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div variants={containerVariants} initial="hidden" animate={inView ? 'visible' : 'hidden'} className="text-center mb-16">
          <motion.h2 variants={itemVariants} id="partner-heading" className="text-4xl sm:text-5xl font-bold text-gray-900 mb-6">
            Partner With Us
          </motion.h2>
          <motion.p variants={itemVariants} className="text-xl text-gray-600 max-w-3xl mx-auto">
            Join a growing network of queer businesses, creators and organisations making a difference.
          </motion.p>
        </motion.div>
        <motion.div variants={containerVariants} initial="hidden" animate={inView ? 'visible' : 'hidden'} className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 mb-16">
          {partners.map((partner, index) => (
            <motion.div key={index} variants={itemVariants} className="bg-white p-8 rounded-2xl border border-gray-200 text-center hover:shadow-xl hover:-translate-y-1 transition-all duration-300">
              <div className="inline-flex p-5 rounded-full bg-primary/10 mb-6">
                <SafeIcon icon={partner.icon} className="w-8 h-8 text-primary" />
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-4">{partner.title}</h3>
              <p className="text-gray-600 leading-relaxed">{partner.description}</p>
            </motion.div>
          ))}
        </motion.div>

        {/* Pricing Section */}
        <div className="mt-24">
          <motion.div variants={containerVariants} initial="hidden" animate={inView ? 'visible' : 'hidden'} className="text-center mb-16">
            <motion.h2 style={{
              color: 'rgb(17, 24, 39)'
            }} variants={itemVariants} className="text-4xl sm:text-5xl font-bold text-gray-900 mb-6">Choose a plan that works for your business</motion.h2>
          </motion.div>
          <motion.div variants={containerVariants} initial="hidden" animate={inView ? 'visible' : 'hidden'} className="grid grid-cols-1 lg:grid-cols-3 gap-8 items-stretch">
            {pricingPlans.map((plan) => (
              <motion.div key={plan.name} variants={itemVariants} className={`relative p-8 rounded-2xl border-2 ${plan.isPopular ? 'border-primary shadow-2xl' : 'border-gray-200 shadow-lg'} bg-white flex flex-col h-full`}>
                {plan.isPopular && (
                  <div className="absolute -top-4 left-1/2 transform -translate-x-1/2 bg-primary text-white px-4 py-1 rounded-full text-sm font-semibold flex items-center space-x-1">
                    <SafeIcon icon={FiZap} className="w-4 h-4" />
                    <span>Most Popular</span>
                  </div>
                )}
                {plan.hasDiscount && (
                  <div className="absolute -top-2 right-4 bg-red-500 text-white px-3 py-1 rounded-full text-xs font-semibold">
                    Limited Offer
                  </div>
                )}
                <div className="flex-grow">
                  <h3 className="text-2xl font-bold mb-2 text-gray-900">{plan.name}</h3>
                  <p className="text-gray-600 mb-6">{plan.description}</p>
                  {plan.name === 'Pro' && (
                    <div className="bg-gray-800 rounded-full p-1 flex items-center mb-6 text-sm">
                      <button onClick={() => setIsProYearly(false)} className={`w-1/2 py-2 font-semibold rounded-full transition-all duration-300 ${!isProYearly ? 'bg-white text-gray-900 shadow-sm' : 'bg-transparent text-gray-400'}`} >
                        Pay Monthly
                      </button>
                      <button onClick={() => setIsProYearly(true)} className={`w-1/2 py-2 font-semibold rounded-full transition-all duration-300 relative ${isProYearly ? 'bg-gradient-to-r from-accent-3 to-primary text-white shadow-lg' : 'bg-transparent text-gray-400'}`} >
                        Pay Yearly
                      </button>
                    </div>
                  )}
                  <div className="mb-6">
                    {plan.name === 'Pro' ? (
                      <div className="space-y-2">
                        <div className="flex items-center justify-center space-x-2">
                          <span className="text-2xl font-medium text-gray-400 line-through">
                            {isProYearly ? plan.price.yearly.original : plan.price.monthly.original}
                          </span>
                          <span className="text-5xl font-bold text-primary">
                            {isProYearly ? plan.price.yearly.discounted : plan.price.monthly.discounted}
                          </span>
                        </div>
                        <span className="text-gray-600">/{isProYearly ? plan.frequency.yearly : plan.frequency.monthly}</span>
                        {isProYearly && <p className="text-primary font-semibold mt-2 text-sm">2 months free!</p>}
                        <div className="bg-green-50 text-green-700 px-3 py-1 rounded-full text-sm font-semibold inline-block mt-2">
                          Save {isProYearly ? '£140' : '£14'} {isProYearly ? 'yearly' : 'monthly'}
                        </div>
                      </div>
                    ) : (
                      <>
                        <span className="text-5xl font-bold text-gray-900">{plan.price}</span>
                        {plan.frequency && <span className="text-gray-600 ml-2">{plan.frequency}</span>}
                      </>
                    )}
                  </div>
                  {plan.isModal ? (
                    <button
                      onClick={() => handleButtonClick(plan)}
                      className={`w-full py-3 rounded-lg font-semibold transition-colors mb-8 ${plan.buttonStyle}`}
                    >
                      {plan.buttonText}
                    </button>
                  ) : (
                    <a 
                      href={plan.buttonLink}
                      target="_blank"
                      rel="noopener noreferrer"
                      className={`w-full py-3 rounded-lg font-semibold transition-colors mb-8 block text-center ${plan.buttonStyle}`}
                    >
                      {plan.buttonText}
                    </a>
                  )}
                  <ul className="space-y-3 text-left">
                    {plan.features.map((feature, index) => (
                      <li key={index} className="flex items-start">
                        <SafeIcon icon={FiCheck} className="w-5 h-5 text-primary mr-3 mt-0.5 flex-shrink-0" />
                        <span className="text-gray-700 text-sm">{feature}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </div>

      {/* Event Submission Modal */}
      <EventSubmissionModal isOpen={isModalOpen} onClose={() => setIsModalOpen(false)} />
    </section>
  );
};

export default PartnerWithUs;